import { resizeProperty } from '@/helpers';
import {
  Accordion,
  Anchor,
  AspectRatio,
  Group,
  Image,
  List,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconChevronDown, IconSquare, IconSquareCheckFilled, IconVideo } from '@tabler/icons-react';
import ReactPlayer from 'react-player';
import { WebsiteSimple } from '../Website/WebsiteSimple';
import { Callout } from './Callout';
import { CodeBlock } from './CodeBlock';
import { Paragraph } from './Paragraph';
import { Quote } from './Quote';
import { TextFragment } from './TextFragment';
import { Embed } from './Embed';

export function Element({
  item,
  formValues,
  preview = false,
  containerWidth = null,
  noMargin = false,
}) {
  switch (item.type) {
    case 'paragraph':
    case 'heading':
      return (
        <Paragraph
          item={item}
          formValues={formValues}
          containerWidth={containerWidth}
          noMargin={noMargin}
        />
      );
    case 'code':
      return <CodeBlock item={item} formValues={formValues} containerWidth={containerWidth} />;
    case 'callout':
      return <Callout item={item} formValues={formValues} containerWidth={containerWidth} />;
    case 'embed':
      return <Embed item={item} />;
    case 'quote':
      return <Quote item={item} formValues={formValues} containerWidth={containerWidth} />;
    case 'video':
      if (preview) {
        return (
          <Group h="100%" w="100%" align="center" justify="center">
            <Tooltip label={item.url}>
              <Anchor href={item.url} target="_blank">
                <IconVideo />
              </Anchor>
            </Tooltip>
          </Group>
        );
      } else {
        return (
          <AspectRatio ratio={16 / 9}>
            <ReactPlayer url={item.videoUrl} controls width="100%" height="100%" />
          </AspectRatio>
        );
      }
    case 'list':
      if (item.listType === 'number') {
        return (
          <List
            mb="md"
            type="ordered"
            withPadding
            spacing={resizeProperty(containerWidth, 20)}
            style={{
              color: formValues.body_font_color,
              fontFamily: formValues.body_font_family,
              fontWeight: formValues.body_font_weight,
              fontSize: resizeProperty(containerWidth, formValues.body_font_size),
              textAlign: formValues.body_alignment,
              fontStyle: formValues.body_italic ? 'italic' : 'normal',
              textDecoration:
                `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
            }}
          >
            {item.children.map((listItem, index) => (
              <List.Item key={index} style={{ lineHeight: 1.5 }}>
                {listItem.children.map((textFragment, index) => (
                  <TextFragment
                    fragment={textFragment}
                    key={index}
                    formValues={formValues}
                    containerWidth={containerWidth}
                  />
                ))}
              </List.Item>
            ))}
          </List>
        );
      } else if (item.listType === 'bullet') {
        return (
          <List
            mb="md"
            withPadding
            spacing={resizeProperty(containerWidth, 20)}
            style={{
              color: formValues.body_font_color,
              fontFamily: formValues.body_font_family,
              fontWeight: formValues.body_font_weight,
              fontSize: resizeProperty(containerWidth, formValues.body_font_size),
              textAlign: formValues.body_alignment,
              fontStyle: formValues.body_italic ? 'italic' : 'normal',
              textDecoration:
                `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
            }}
          >
            {item.children.map((listItem, index) => (
              <List.Item key={index} style={{ lineHeight: 1.5 }}>
                {listItem.children.map((textFragment, index) => (
                  <TextFragment
                    fragment={textFragment}
                    key={index}
                    formValues={formValues}
                    containerWidth={containerWidth}
                  />
                ))}
              </List.Item>
            ))}
          </List>
        );
      } else if (item.listType === 'check') {
        const bodyFontSize = resizeProperty(containerWidth, formValues.body_font_size);
        return (
          <List
            mb="md"
            withPadding
            spacing={resizeProperty(containerWidth, 20)}
            style={{
              color: formValues.body_font_color,
              fontFamily: formValues.body_font_family,
              fontWeight: formValues.body_font_weight,
              fontSize: bodyFontSize,
              textAlign: formValues.body_alignment,
              fontStyle: formValues.body_italic ? 'italic' : 'normal',
              textDecoration:
                `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
            }}
          >
            {item.children.map((listItem, index) => (
              <List.Item
                key={index}
                style={{ lineHeight: 1.5 }}
                styles={{
                  itemWrapper: { alignItems: 'start' },
                  itemIcon: { marginTop: Math.round(bodyFontSize / 6) },
                  itemLabel: { textDecoration: listItem.checked && 'line-through' },
                }}
                icon={
                  <ThemeIcon
                    size={bodyFontSize}
                    variant="default"
                    style={{ color: formValues.body_font_color, border: 0, background: 0 }}
                  >
                    {listItem.checked ? (
                      <IconSquareCheckFilled
                        style={{ width: bodyFontSize, height: bodyFontSize }}
                      />
                    ) : (
                      <IconSquare style={{ width: bodyFontSize, height: bodyFontSize }} />
                    )}
                  </ThemeIcon>
                }
              >
                {listItem.children.map((textFragment, index) => (
                  <TextFragment
                    fragment={textFragment}
                    key={index}
                    formValues={formValues}
                    containerWidth={containerWidth}
                  />
                ))}
              </List.Item>
            ))}
          </List>
        );
      }
    case 'toggle':
      return (
        <Accordion
          variant="contained"
          chevron={
            <IconChevronDown size={resizeProperty(containerWidth, formValues.body_font_size)} />
          }
          chevronSize={resizeProperty(containerWidth, formValues.body_font_size)}
          chevronPosition="left"
          mb="lg"
          styles={{
            item: {
              backgroundColor: 'transparent',
            },
            control: {
              backgroundColor: 'transparent',
            },
          }}
        >
          <Accordion.Item value="0">
            <Accordion.Control
              style={{
                color: formValues.body_font_color,
                fontFamily: formValues.body_font_family,
                fontSize: resizeProperty(containerWidth, formValues.body_font_size),
                textAlign: formValues.body_alignment,
                fontStyle: formValues.body_italic ? 'italic' : 'normal',
                textDecoration:
                  `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
              }}
            >
              {item.children[0].children.map((element, index) => (
                <Element
                  key={index}
                  item={element}
                  formValues={formValues}
                  containerWidth={containerWidth}
                  noMargin
                />
              ))}
            </Accordion.Control>
            <Accordion.Panel
              style={{
                color: formValues.body_font_color,
                fontFamily: formValues.body_font_family,
                fontWeight: formValues.body_font_weight,
                fontSize: resizeProperty(containerWidth, formValues.body_font_size),
                textAlign: formValues.body_alignment,
                fontStyle: formValues.body_italic ? 'italic' : 'normal',
                textDecoration:
                  `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
              }}
            >
              {item.children[1].children.map((element, index) => (
                <Element
                  key={index}
                  item={element}
                  formValues={formValues}
                  containerWidth={containerWidth}
                  noMargin
                />
              ))}
            </Accordion.Panel>
          </Accordion.Item>
        </Accordion>
      );
    case 'table':
      return (
        <Table
          style={{
            color: formValues.body_font_color,
            fontFamily: formValues.body_font_family,
            fontWeight: formValues.body_font_weight,
            fontSize: resizeProperty(containerWidth, formValues.body_font_size),
            textAlign: formValues.body_alignment,
            fontStyle: formValues.body_italic ? 'italic' : 'normal',
            textDecoration:
              `${formValues.body_underlined ? 'underline ' : ''}${formValues.body_strikethrough ? 'line-through' : ''}`.trim(),
          }}
        >
          <Table.Thead>
            <Table.Tr>
              {item.children[0].children
                .map((cell, cellIndex) => {
                  if (cell.colSpan === 0 && cell.rowSpan === 0) return;

                  return (
                    <Table.Th
                      key={cellIndex}
                      rowSpan={cell.rowSpan}
                      colSpan={cell.colSpan}
                      style={{
                        backgroundColor: cell.backgroundColor,
                      }}
                    >
                      {cell.children.map((d, index) => (
                        <Paragraph item={d} key={index} containerWidth={containerWidth} />
                      ))}
                    </Table.Th>
                  );
                })
                .filter((cell) => cell)}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {item.children.slice(1).map((row, rowIndex) => (
              <Table.Tr key={rowIndex}>
                {row.children.map((cell, cellIndex) => {
                  if (cell.colSpan === 0 && cell.rowSpan === 0) return;

                  return (
                    <Table.Td key={cellIndex} rowSpan={cell.rowSpan} colSpan={cell.colSpan}>
                      {cell.children.map((d, index) => (
                        <Paragraph
                          item={d}
                          key={index}
                          formValues={formValues}
                          containerWidth={containerWidth}
                        />
                      ))}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      );
    case 'bookmark':
      return (
        <UnstyledButton
          w="100%"
          style={{
            color: formValues.body_font_color,
            fontFamily: formValues.body_font_family,
          }}
          onClick={() =>
            modals.open({
              children: <WebsiteSimple src={item.url} closeModals={modals.closeAll} />,
              fullScreen: true,
              withCloseButton: false,
            })
          }
        >
          <Paper
            shadow="md"
            p="lg"
            radius="md"
            mb="lg"
            bg="transparent"
            withBorder
            style={{ borderColor: formValues.body_font_color }}
          >
            <Stack gap="md" mb="md">
              <Title fz={resizeProperty(containerWidth, formValues.h2_font_size)}>
                {item.title}
              </Title>
              <Text
                fz={resizeProperty(containerWidth, formValues.body_font_size * 0.7)}
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.description}
              </Text>
              <Group>
                <Image src={item.favicon} alt="Favicon" />
                <Text
                  fz={resizeProperty(containerWidth, formValues.body_font_size * 0.7)}
                  truncate="end"
                  style={{ flex: 1 }}
                >
                  {item.url}
                </Text>
              </Group>
            </Stack>
            <Image
              src={item.cover}
              alt="Preview"
              style={{ objectFit: 'cover', width: '100%', height: '100%', borderRadius: '2px' }}
            />
          </Paper>
        </UnstyledButton>
      );
    default:
      return null;
  }
}
